import { Disclaimer, H5, P, lightTheme } from '@vgrubs/components';
import { KeyboardEvent, ReactNode } from 'react';
import {
  BannerAction,
  BannerIcon,
  BannerSurface,
  BannerText,
} from './OutstandingBanners.styled';

type Props = {
  /** Red when critical, yellow otherwise - the tablet's two banner colours. */
  critical: boolean;
  title?: string | null;
  message?: string | null;
  /** A quieter line under the message (who shares a vDrive balance). */
  note?: string | null;
  /** Anything else under the text - Location Problems puts its chips here. */
  children?: ReactNode;
  /** Right of the text on desktop, under it on mobile - a Pay button. */
  action?: ReactNode;
  /**
   * Makes the whole strip the click target, for a banner with something to do
   * and no button. Ignored when there is an `action`: then only it acts.
   */
  onActivate?: (() => void) | null;
};

/**
 * One notice strip, drawn like the tablet's banner: a round "!", the title in
 * the severity's colour, the message, and the action on the right.
 *
 * The billing banners (OutstandingBanners) are made of these, and so is the
 * Dashboard's critical System Problems bar, so an alarm looks the same
 * whatever it is about.
 */
export const NoticeBanner = ({
  critical,
  title,
  message,
  note,
  action,
  onActivate,
  children,
}: Props) => {
  const activate = action ? null : onActivate ?? null;
  const clickable = !!activate;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate?.();
    }
  };

  return (
    <BannerSurface
      $critical={critical}
      $clickable={clickable}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={activate ?? undefined}
      onKeyDown={clickable ? onKeyDown : undefined}
    >
      <BannerIcon $critical={critical} aria-hidden>
        !
      </BannerIcon>
      <BannerText>
        {title && (
          <H5
            $m="0 0 4px 0"
            $color={critical ? lightTheme.primary : lightTheme.darkYellow}
          >
            {title}
          </H5>
        )}
        {message && <P $m="0">{message}</P>}
        {note && (
          <Disclaimer $m="4px 0 0 0" $color={lightTheme.darkGray}>
            {note}
          </Disclaimer>
        )}
        {children}
      </BannerText>
      {action && <BannerAction>{action}</BannerAction>}
    </BannerSurface>
  );
};
